import Layout from '@/components/Layout'
import { useState, useEffect, useRef, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import api from '@/services/api'
import { API_ENDPOINTS } from '@/config/api'
import './style.css'
import { gamesConfig } from '../../config/casinoConfig'
import { mapLastResults } from '../../utils/casinoUtils'
import CasinoResultModal from '@/components/casinoResultModal'
import { getDetailResults } from '@/services/casinoApi'

// Import result components
import Teen20c from '@/components/casinoResultModal/games/teen20c'
import Teen from '@/components/casinoResultModal/games/teen'
import AB20 from '@/components/casinoResultModal/games/ab20'
import Baccarat from '@/components/casinoResultModal/games/baccarat'
import ThreeCardJ from '@/components/casinoResultModal/games/3cardj'
import AAA from '@/components/casinoResultModal/games/aaa'
import CasinoWar from '@/components/casinoResultModal/games/casinoWar'
import Sicbo from '@/components/casinoResultModal/games/sicbo'
import DT20 from '@/components/casinoResultModal/games/dt20'
import Lucky7 from '@/components/casinoResultModal/games/lucky7'
import Lucky15 from '@/components/casinoResultModal/games/lucky15'
import Poker from '@/components/casinoResultModal/games/poker'
import Race17 from '@/components/casinoResultModal/games/race17'
import Dum10 from '@/components/casinoResultModal/games/dum10'

/**
 * CasinoResultReport Component
 * 
 * Page for viewing casino result reports.
 * Ports logic from frontend CasinoResult component.
 */
function CasinoResultReport() {
  const navigate = useNavigate()
  const { gameName } = useParams() // Get game name from URL
  const [entriesPerPage, setEntriesPerPage] = useState(50)
  const [searchValue, setSearchValue] = useState('')

  // Find default game path based on URL param or default to first game
  const defaultGamePath = useMemo(() => {
    if (gameName) {
      const found = gamesConfig.find(g => g.path.includes(gameName))
      if (found) return found.path.split('/').pop() // Extract 'teen62' from '/casino/teen62'
      return gameName
    }
    const firstGame = gamesConfig[0]?.path.split('/').pop()
    return firstGame || 'teen62'
  }, [gameName])

  const [accountType, setAccountType] = useState(defaultGamePath)
  const [selectedDate, setSelectedDate] = useState('')
  const [tableData, setTableData] = useState([])
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const dateInputRef = useRef(null)
  
  // Modal state
  const [showModal, setShowModal] = useState(false)
  const [selectedRoundId, setSelectedRoundId] = useState(null)
  const [detailResult, setDetailResult] = useState(null)
  const [modalLoading, setModalLoading] = useState(false)

  // Helper function to format date as YYYY-MM-DD
  const formatDate = (date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  // Initialize date to today
  useEffect(() => {
    const today = new Date()
    setSelectedDate(formatDate(today))
  }, [])

  // Update accountType when gameName changes
  useEffect(() => {
    if (gameName) {
      // Try to find the game in config to get exact ID/type if needed, 
      // or just trust the param if it matches expected format
      // In frontend: getGameRouteName extracts the last part.
      // We do similar logic here.
      setAccountType(gameName)
      // Automatically load data when URL param changes
      handleLoadData(gameName)
    } else {
      // Load default if no param
      handleLoadData(accountType)
    }
  }, [gameName])

  // Wrapper for submit button
  const handleSubmit = () => {
    handleLoadData(accountType)
  }

  // Core data loading function
  const handleLoadData = async (gameType) => {
    if (!gameType) return

    setLoading(true)
    try {
      // Call public API directly acting as client
      // Uses https://api.reddyannaexch.cloud/api/casino/casino/last-results

      const payload = {
        type: gameType
      }

      // Use configured API client
      const response = await api.post(
        API_ENDPOINTS.CASINO.LAST_RESULTS,
        payload
      )

      if (response && response.data) {
        // Map API response using utility from frontend
        const mappedData = mapLastResults(response.data, gameType)

        // Transform for table display
        const displayData = mappedData.map((item) => ({
          roundId: item.mid || 'N/A',
          winner: item.win || 'N/A',
          // Public API doesn't seem to return createdAt in the res array based on mapLastResults,
          // it returns mid and win. We'll leave createdAt empty or remove it.
          // mapLastResults returns { mid, win, originalWin }
        }))

        setTableData(displayData)
      } else {
        setTableData([])
      }
    } catch (error) {
      console.error('Error loading data:', error)
      setTableData([])
    } finally {
      setLoading(false)
    }
  }

  // Filter and paginate data
  const filteredData = useMemo(() => {
    let filtered = tableData

    if (searchValue) {
      filtered = filtered.filter(
        (item) =>
          item.roundId.toString().toLowerCase().includes(searchValue.toLowerCase()) ||
          item.winner.toString().toLowerCase().includes(searchValue.toLowerCase())
      )
    }

    return filtered
  }, [tableData, searchValue])

  const totalPages = Math.ceil(filteredData.length / entriesPerPage)
  const startIndex = (currentPage - 1) * entriesPerPage
  const endIndex = startIndex + entriesPerPage
  const paginatedData = filteredData.slice(startIndex, endIndex)

  // Function to render result component based on game type
  const renderResultComponent = (gameType, detailResult, roundId, matchTime) => {
    switch (gameType) {
      case 'teen20c':
        return <Teen20c detailResult={detailResult} roundId={roundId} matchTime={matchTime} />
      
      case 'teen':
      case 'teen62':
      case 'teen20':
      case 'teen8':
      case 'teen9':
      case 'teen32':
      case 'teen33':
      case 'teen41':
      case 'teen42':
      case 'teen6':
      case 'teen3':
      case 'teen1':
      case 'teen120':
      case 'teenunique':
      case 'poison':
      case 'poison20':
      case 'joker1':
      case 'joker20':
      case 'joker120':
      case 'teenjoker':
      case 'teenmuf':
        return <Teen detailResult={detailResult} roundId={roundId} matchTime={matchTime} />
      
      case 'ab20':
      case 'ab3':
      case 'ab4':
        return <AB20 detailResult={detailResult} roundId={roundId} matchTime={matchTime} />
      
      case 'abj':
        return <AB20 detailResult={detailResult} roundId={roundId} matchTime={matchTime} />
      
      case 'baccarat':
      case 'baccarat2':
      case 'baccarat29':
      case 'teensin': // 29 CARD BACCARAT - uses same layout as Baccarat
        return <Baccarat detailResult={detailResult} roundId={roundId} matchTime={matchTime} />
      
      case '3cardj':
        return <ThreeCardJ detailResult={detailResult} roundId={roundId} matchTime={matchTime} />
      
      case 'aaa':
      case 'aaa2':
        return <AAA detailResult={detailResult} roundId={roundId} matchTime={matchTime} />
      
      case 'war':
        return <CasinoWar detailResult={detailResult} roundId={roundId} matchTime={matchTime} />
      
      case 'sicbo':
      case 'sicbo2':
        return <Sicbo detailResult={detailResult} roundId={roundId} matchTime={matchTime} />
      
      case 'dt20':
      case 'dt6':
      case 'dt202':
      case 'dtl20':
        return <DT20 detailResult={detailResult} roundId={roundId} matchTime={matchTime} />
      
      case 'lucky7':
      case 'lucky7eu':
      case 'lucky7eu2':
        return <Lucky7 detailResult={detailResult} roundId={roundId} matchTime={matchTime} />
      
      case 'lucky15':
        return <Lucky15 detailResult={detailResult} roundId={roundId} matchTime={matchTime} />
      
      case 'poker':
      case 'poker20':
      case 'poker6':
        return <Poker detailResult={detailResult} roundId={roundId} matchTime={matchTime} />
      
      case 'race17':
        return <Race17 detailResult={detailResult} roundId={roundId} matchTime={matchTime} />
      
      case 'dum10':
        return <Dum10 detailResult={detailResult} roundId={roundId} matchTime={matchTime} />
      
      // Add more cases as we create components
      default:
        return (
          <div className="p-4">
            <h6 className="text-right mb-3">
              <b>Round Id:</b> {roundId}
            </h6>
            {detailResult ? (
              <div>
                <pre className="text-white bg-dark p-3 rounded" style={{ fontSize: '12px', overflow: 'auto' }}>
                  {JSON.stringify(detailResult, null, 2)}
                </pre>
              </div>
            ) : (
              <p className="text-white">No detail data available</p>
            )}
          </div>
        )
    }
  }

  // Handle round ID click - fetch detail and show modal
  const handleRoundIdClick = async (roundId) => {
    if (!roundId || roundId === 'N/A') return
    
    setSelectedRoundId(roundId)
    setShowModal(true)
    setModalLoading(true)
    setDetailResult(null)

    try {
      const response = await getDetailResults(accountType, roundId.toString())
      if (response?.success) {
        if (response?.data?.data?.t1) {
          setDetailResult(response.data.data.t1)
        } else if (response?.data?.t1) {
          setDetailResult(response.data.t1)
        } else if (response?.data?.data) {
          setDetailResult(response.data.data)
        }
      }
    } catch (error) {
      console.error('Error fetching detail results:', error)
    } finally {
      setModalLoading(false)
    }
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setSelectedRoundId(null)
    setDetailResult(null)
  }

  // Get game title for modal
  const getGameTitle = () => {
    const game = gamesConfig.find(g => g.path.split('/').pop() === accountType)
    return game?.title || 'Casino Result'
  }

  // Handle game change in dropdown
  const handleGameChange = (e) => {
    const newGame = e.target.value
    setAccountType(newGame)
    // Update URL to match selection
    navigate(`/admin/reports/casinoresult/${newGame}`)
  }

  return (
    <Layout>
      <div className="w-full bg-[#171b36] min-h-screen p-4">
        {/* Header Section */}
        <div
          className="shadow-sm "
        >
          <div className="flex justify-between items-start mb-2">
            <div
              style={{ color: "#cae0e8", fontSize: "24px" }}
              className="font-semibold"
            >
              Casino Result Report
            </div>

          </div>
          <div className="w-full h-px bg-white mb-2"></div>
        </div>


        {/* Filter Section */}
        <div className=" mb-4 rounded-sm">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex flex-col">
              <label className="text-white text-sm mb-1">From</label>
              <div className="relative">
                <input
                  ref={dateInputRef}
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="px-3 py-2 bg-white text-black rounded-sm border-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled // Disabled as public API doesn't filter by date
                  title="Date filtering not supported for live results"
                />

              </div>
            </div>

            <div className="flex flex-col">
              <label className="text-white text-sm mb-1">Account Type</label>
              <select
                value={accountType}
                onChange={handleGameChange}
                className="px-3 py-2 bg-white text-black rounded-sm border-none focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[200px]"
              >
                <option value="" disabled>Select Game</option>
                {gamesConfig.map((game, index) => {
                  // Extract clean value from path, e.g. /casino/teen62 -> teen62
                  const value = game.path.split('/').pop()
                  return (
                    <option key={index} value={value}>
                      {game.title}
                    </option>
                  )
                })}
              </select>
            </div>

            <button
              onClick={handleSubmit}
              disabled={loading}
              className="px-6 py-2 bg-[#333d79] text-white font-medium rounded-sm hover:bg-[#2d3569] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Loading...' : 'Load'}
            </button>
          </div>
        </div>

        {/* Table Controls */}
        <div className="mb-4 rounded-sm">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <span className="text-white text-sm font-bold">Show</span>
              <select
                value={entriesPerPage}
                onChange={(e) => {
                  setEntriesPerPage(Number(e.target.value))
                  setCurrentPage(1)
                }}
                className="px-2 py-1 bg-white text-black rounded-sm border-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="250">250</option>
                <option value="500">500</option>
              </select>
              <span className="text-white text-sm font-bold">entries</span>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-white text-sm font-bold">Search:</label>
              <input
                type="text"
                value={searchValue}
                onChange={(e) => {
                  setSearchValue(e.target.value)
                  setCurrentPage(1)
                }}
                placeholder="Type to Search"
                className="px-3 py-1 bg-white text-black rounded-sm border-none focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-500"
              />
            </div>
          </div>
        </div>

        {/* Table Section */}
        <div

          className="shadow-sm py-2"
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="text-left px-2 py-1 text-white font-bold text-[14px]">
                    Round Id
                  </th>
                  <th className="text-left px-2 py-1 text-white font-bold text-[14px]">
                    Winner
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="2" className="px-2 py-8 text-center text-gray-400">
                      Loading...
                    </td>
                  </tr>
                ) : paginatedData.length === 0 ? (
                  <tr>
                    <td colSpan="2" className="px-2 py-8 text-center text-gray-400">
                      There are no records to show
                    </td>
                  </tr>
                ) : (
                  paginatedData.map((row, index) => (
                    <tr
                      key={row.roundId + index}
                      className={`transition-colors ${index % 2 === 0 ? "bg-[rgba(245,245,245,0.1)]" : ""
                        }`}
                    >
                      <td className="py-1 px-2 text-white text-[13px]">
                        <button
                          onClick={() => handleRoundIdClick(row.roundId)}
                          className="text-[#2ce5dd] hover:text-blue-300 underline cursor-pointer"
                        >
                          {row.roundId}
                        </button>
                      </td>
                      <td className="py-1 px-2 text-white text-[13px]">
                        {row.winner}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="my-1 p-m-l col">
              <ul
                role="menubar"
                aria-disabled="false"
                aria-label="Pagination"
                className="pagination my-0 b-pagination justify-content-end"
              >
                <li
                  role="presentation"
                  aria-hidden={currentPage === 1 ? "true" : "false"}
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  {currentPage === 1 ? (
                    <span
                      role="menuitem"
                      aria-label="Go to first page"
                      aria-disabled="true"
                      className="page-link"
                    >
                      «
                    </span>
                  ) : (
                    <button
                      role="menuitem"
                      type="button"
                      aria-label="Go to first page"
                      onClick={() => setCurrentPage(1)}
                      className="page-link"
                    >
                      «
                    </button>
                  )}
                </li>
                <li
                  role="presentation"
                  aria-hidden={currentPage === 1 ? "true" : "false"}
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  {currentPage === 1 ? (
                    <span
                      role="menuitem"
                      aria-label="Go to previous page"
                      aria-disabled="true"
                      className="page-link"
                    >
                      ‹
                    </span>
                  ) : (
                    <button
                      role="menuitem"
                      type="button"
                      aria-label="Go to previous page"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      className="page-link"
                    >
                      ‹
                    </button>
                  )}
                </li>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <li
                    key={page}
                    role="presentation"
                    className={`page-item ${currentPage === page ? "active" : ""}`}
                  >
                    <button
                      role="menuitemradio"
                      type="button"
                      aria-label={`Go to page ${page}`}
                      aria-checked={currentPage === page}
                      aria-posinset={page}
                      aria-setsize={totalPages}
                      tabIndex={currentPage === page ? 0 : -1}
                      onClick={() => setCurrentPage(page)}
                      className="page-link"
                    >
                      {page}
                    </button>
                  </li>
                ))}
                <li
                  role="presentation"
                  aria-hidden={currentPage === totalPages ? "true" : "false"}
                  className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                >
                  {currentPage === totalPages ? (
                    <span
                      role="menuitem"
                      aria-label="Go to next page"
                      aria-disabled="true"
                      className="page-link"
                    >
                      ›
                    </span>
                  ) : (
                    <button
                      role="menuitem"
                      type="button"
                      aria-label="Go to next page"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      className="page-link"
                    >
                      ›
                    </button>
                  )}
                </li>
                <li
                  role="presentation"
                  aria-hidden={currentPage === totalPages ? "true" : "false"}
                  className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                >
                  {currentPage === totalPages ? (
                    <span
                      role="menuitem"
                      aria-label="Go to last page"
                      aria-disabled="true"
                      className="page-link"
                    >
                      »
                    </span>
                  ) : (
                    <button
                      role="menuitem"
                      type="button"
                      aria-label="Go to last page"
                      onClick={() => setCurrentPage(totalPages)}
                      className="page-link"
                    >
                      »
                    </button>
                  )}
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Result Modal */}
      <CasinoResultModal
        show={showModal}
        onHide={handleCloseModal}
        title={getGameTitle()}
        roundId={selectedRoundId}
        loading={modalLoading}
      >
        {renderResultComponent(accountType, detailResult, selectedRoundId, detailResult?.mtime || '')}
      </CasinoResultModal>
    </Layout>
  )
}

export default CasinoResultReport
